// SemanticUI-free pre-@plone/components

import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Grid } from 'semantic-ui-react';
import { doesNodeContainClick } from 'semantic-ui-react/dist/commonjs/lib';
import { useIntl, defineMessages, injectIntl } from 'react-intl';
import cx from 'classnames';
import { UniversalLink } from '@plone/volto/components';
import { getBaseUrl, hasApiExpander } from '@plone/volto/helpers';
import config from '@plone/volto/registry';
import IntranetSearchWidget from '../SearchWidget/IntranetSearchWidget';
import { useBreakpoint } from '../../../../helpers/useMedia';
import { getNavigation } from '@plone/volto/actions';
import NavItem from '@plone/volto/components/theme/Navigation/NavItem';
import { ButtonLink } from '../../../../components/Button';

import { Icon } from '@plone/volto/components';
import navArrowDown from '../../../../assets/icons/nav-arrow-down.svg';

const messages = defineMessages({
  closeMenu: {
    id: 'Close menu',
    defaultMessage: 'Close menu',
  },
  openFatMenu: {
    id: 'Open menu',
    defaultMessage: 'Open menu',
  },
  factfigTitle: {
    id: 'factfigtitle',
    defaultMessage: 'Our environment: facts and figures',
  },
  factfigText: {
    id: 'factfigtext',
    defaultMessage:
      'What is the air quality like in Flanders? What is the current chance of a flood? These and many more questions are answered with clear figures and maps from your area.',
  },
  factfigButton: {
    id: 'factfigbutton',
    defaultMessage: 'View all facts and figures',
  },
  envpolicyTitle: {
    id: 'envpolicytitle',
    defaultMessage: 'How do we work towards a better environment?',
  },
  envpolicyText: {
    id: 'envpolicytext',
    defaultMessage:
      'What legislation and standards does the government use for water, air and climate adaptation in Flanders?',
  },
  envpolicyTextbr: {
    id: 'envpolicytextbr',
    defaultMessage:
      'Discover the rules and policies with which we work towards a better living environment.',
  },
  servprodTitle: {
    id: 'servprodtitle',
    defaultMessage: 'How can we help you?',
  },
  servprodText: {
    id: 'servprodtext',
    defaultMessage: 'Need advice about your permit or subsidy application?',
  },
  servprodTextbr: {
    id: 'servprodtextbr',
    defaultMessage: 'Submit your tax return or an application to VMM',
  },
  servprodButton: {
    id: 'servprodbutton',
    defaultMessage: 'View all services and products',
  },
  projectsTitle: {
    id: 'projectstitle',
    defaultMessage: 'Where are we at work?',
  },
  projectsText: {
    id: 'projectstext',
    defaultMessage:
      'The VMM works on many environmental projects with numerous local and international partners.',
  },
  projectsButton: {
    id: 'projectsbutton',
    defaultMessage: 'View all projects',
  },
  tipsTitle: {
    id: 'tipstitle',
    defaultMessage: 'What can you do?',
  },
  tipsText: {
    id: 'tipstext',
    defaultMessage:
      'A better environment starts with you. Discover our tips that you can get started with in your neighborhood.',
  },
});

const Navigation = ({ pathname }) => {
  const [currentOpenIndex, setCurrentOpenIndex] = useState(null);
  const navigation = useRef(null);
  const dispatch = useDispatch();
  const intl = useIntl();
  const enableFatMenu = config.settings.enableFatMenu;
  const lang = useSelector((state) => state.intl.locale);
  const token = useSelector((state) => state.userSession.token, shallowEqual);
  // Restrict the main menu items to the length of menuBodies: 5.
  const items = useSelector(
    (state) => state.navigation.items.slice(0, 5),
    shallowEqual,
  );

  const openMenu = useCallback((index) => {
    setCurrentOpenIndex((s) => (s === index ? null : index));
  }, []);

  const closeMenu = useCallback(() => {
    setCurrentOpenIndex(null);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navigation.current && doesNodeContainClick(navigation.current, e))
        return;
      closeMenu();
    };

    document.addEventListener('mousedown', handleClickOutside, false);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, [closeMenu]);

  useEffect(() => {
    if (!hasApiExpander('navigation', getBaseUrl(pathname))) {
      dispatch(getNavigation(getBaseUrl(pathname), config.settings.navDepth));
    }
  }, [pathname, token, dispatch]);

  const isActive = (url) => {
    return (url === '' && pathname === '/') || (url !== '' && pathname === url);
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        closeMenu();
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [closeMenu]);

  const isBigScreen = useBreakpoint('xl');
  const collapseSearch = useBreakpoint(1300);

  const menuBodies = [
    () => (
      <div className="headerTitleBlock">
        <h2>{intl.formatMessage(messages.factfigTitle)}</h2>
        <p>{intl.formatMessage(messages.factfigText)}</p>
        <ButtonLink
          color="secondary"
          size="large"
          halfrounded="true"
          icon
          href="/feiten-cijfers/catalogus-feiten-cijfers"
          onClick={() => closeMenu()}
        >
          {intl.formatMessage(messages.factfigButton)}
        </ButtonLink>
      </div>
    ),
    () => (
      <div className="headerTitleBlock">
        <h2>{intl.formatMessage(messages.envpolicyTitle)}</h2>
        <p>
          {intl.formatMessage(messages.envpolicyText)} <br />
          {intl.formatMessage(messages.envpolicyTextbr)}
        </p>
      </div>
    ),
    () => (
      <div className="headerTitleBlock">
        <h2>{intl.formatMessage(messages.servprodTitle)}</h2>
        <p>
          {intl.formatMessage(messages.servprodText)}
          <br />
          {intl.formatMessage(messages.servprodTextbr)}
        </p>
        <ButtonLink
          color="secondary"
          size="large"
          halfrounded="true"
          icon
          href="/diensten-producten"
          onClick={() => closeMenu()}
        >
          {intl.formatMessage(messages.servprodButton)}
        </ButtonLink>
      </div>
    ),
    () => (
      <div className="headerTitleBlock">
        <h2>{intl.formatMessage(messages.projectsTitle)}</h2>
        <p>{intl.formatMessage(messages.projectsText)}</p>
        <ButtonLink
          color="secondary"
          size="large"
          halfrounded="true"
          icon
          href="/projecten"
          onClick={() => closeMenu()}
        >
          {intl.formatMessage(messages.projectsButton)}
        </ButtonLink>
      </div>
    ),
    () => (
      <div className="headerTitleBlock">
        <h2>{intl.formatMessage(messages.tipsTitle)}</h2>
        <p>{intl.formatMessage(messages.tipsText)}</p>
      </div>
    ),
    () => <div className="headerTitleBlock"></div>,
    () => <div className="headerTitleBlock"></div>,
    () => <div className="headerTitleBlock"></div>,
  ];

  return (
    <nav
      id="navigation"
      aria-label="navigation"
      className="navigation"
      style={{ flexDirection: 'row', justifyContent: 'space-between' }}
      ref={navigation}
    >
      <ul className="desktop-menu">
        {items.map((item, index) => (
          <li key={item.url}>
            {enableFatMenu ? (
              <>
                <button
                  onClick={() => openMenu(index)}
                  className={cx('item', {
                    active:
                      currentOpenIndex === index ||
                      (currentOpenIndex !== null && pathname === item.url),
                  })}
                  aria-label={intl.formatMessage(messages.openFatMenu)}
                  aria-expanded={currentOpenIndex === index ? true : false}
                >
                  {item.title}
                  <Icon name={navArrowDown} size="7px" color="white" />
                </button>

                <div className="submenu-wrapper">
                  <div
                    className={cx('submenu', {
                      active: currentOpenIndex === index,
                    })}
                  >
                    <div className="submenu-inner">
                      {menuBodies[index]()}
                      <Grid className="navItemsBlock" columns={2}>
                        {item.items &&
                          item.items.length > 0 &&
                          item.items.slice(0, 4).map((subitem) => (
                            <Grid.Column
                              computer={12}
                              largeScreen={6}
                              widescreen={6}
                              key={subitem.url}
                              className="subitem-wrapper"
                            >
                              <UniversalLink
                                className="subitem-header"
                                onClick={() => closeMenu()}
                                item={subitem}
                              >
                                <p
                                  className={cx({
                                    current: isActive(subitem.url),
                                  })}
                                >
                                  {subitem.nav_title || subitem.title}
                                </p>
                                <svg
                                  width="20"
                                  height="25"
                                  viewBox="0 0 20 25"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <circle
                                    opacity="0.1"
                                    cx="10"
                                    cy="15"
                                    r="10"
                                    fill="#1D9BF0"
                                  />
                                  <path
                                    d="M8.52734 11L12.1273 14.6C12.182 14.6513 12.2256 14.7133 12.2554 14.7821C12.2851 14.8509 12.3005 14.925 12.3005 15C12.3005 15.075 12.2851 15.1491 12.2554 15.2179C12.2256 15.2867 12.182 15.3487 12.1273 15.4L8.52734 19"
                                    stroke="#1D9BF0"
                                    strokeWidth="1.71429"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </UniversalLink>
                              <span className="pageDescription">
                                {subitem.description}
                              </span>
                            </Grid.Column>
                          ))}
                      </Grid>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <NavItem
                collapsable={!isBigScreen}
                item={item}
                lang={lang}
                key={item.url}
              />
            )}
          </li>
        ))}
      </ul>
      <IntranetSearchWidget collapsable={!collapseSearch} />
    </nav>
  );
};

Navigation.propTypes = {
  pathname: PropTypes.string.isRequired,
};

Navigation.defaultProps = {
  token: null,
};

export default injectIntl(Navigation);
